// src/pages/markets/components/SignalsPanel.tsx

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import InsightsIcon from '@mui/icons-material/Insights';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import { Virtuoso } from 'react-virtuoso';
import type { SignalItem } from '../api/types';
import { MARKET_SIGNAL_TYPES } from '../api/types';
import { useSignalInteraction } from '../context/SignalInteractionContext';

import {
    SIGNAL_TYPE_LABELS,
    SIGNAL_TYPE_SHORT,
} from './signalConstants';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface SignalsPanelProps {
    signals: SignalItem[];
    loading: boolean;
    error?: string | null;
    /** Show which market a signal belongs to — pass true in event-wide mode */
    showMarketBadge?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRelativeTime(ts: string | Date): string {
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

function formatExactDate(ts: string | Date): string {
    const d = new Date(ts);
    const now = new Date();
    const isThisYear = d.getFullYear() === now.getFullYear();
    const date = d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        ...(isThisYear ? {} : { year: 'numeric' }),
    });
    const time = d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    return `${date} · ${time}`;
}

function shortQ(q: string, max = 30): string {
    return q && q.length > max ? q.slice(0, max) + '…' : q;
}

function scoreStrength(score: number): 'low' | 'med' | 'high' {
    if (score >= 0.66) return 'high';
    if (score >= 0.33) return 'med';
    return 'low';
}

// ─── Filter state types ───────────────────────────────────────────────────────

type DirectionFilter = 'bullish' | 'bearish' | 'neutral' | null;
type StrengthFilter = 'low' | 'med' | 'high' | null;

// ─── Summary Stats ────────────────────────────────────────────────────────────

interface TypeStats {
    bullish: number;
    bearish: number;
    neutral: number;
    total: number;
}

interface SignalSummary {
    total: number;
    openCount: number;
    bullishCount: number;
    bearishCount: number;
    neutralCount: number;
    bullishPct: number;
    bearishPct: number;
    /** signal_type → { bullish, bearish, neutral, total } */
    byType: Map<string, TypeStats>;
    /** 'low' | 'med' | 'high' → count */
    byStrength: { low: number; med: number; high: number };
}

function computeSummary(signals: SignalItem[]): SignalSummary {
    const byType = new Map<string, TypeStats>();
    let openCount = 0;
    let bullishCount = 0;
    let bearishCount = 0;
    let neutralCount = 0;
    const byStrength = { low: 0, med: 0, high: 0 };

    for (const s of signals) {
        if (s.status === 'open') openCount++;

        const dir = s.direction;
        if (dir === 'bullish') bullishCount++;
        else if (dir === 'bearish') bearishCount++;
        else neutralCount++;

        const bucket = scoreStrength(s.score);
        byStrength[bucket]++;

        if (!byType.has(s.signal_type)) {
            byType.set(s.signal_type, { bullish: 0, bearish: 0, neutral: 0, total: 0 });
        }
        const ts = byType.get(s.signal_type)!;
        ts.total++;
        if (dir === 'bullish') ts.bullish++;
        else if (dir === 'bearish') ts.bearish++;
        else ts.neutral++;
    }

    const total = signals.length;
    return {
        total,
        openCount,
        bullishCount,
        bearishCount,
        neutralCount,
        bullishPct: total > 0 ? Math.round((bullishCount / total) * 100) : 0,
        bearishPct: total > 0 ? Math.round((bearishCount / total) * 100) : 0,
        byType,
        byStrength,
    };
}

// ─── Signal detail (expanded) ─────────────────────────────────────────────────

function SignalDetail({ signal }: { signal: SignalItem }) {
    const theme = useTheme();
    const bg = theme.palette.mode === 'dark' ? alpha('#fff', 0.04) : alpha('#000', 0.03);

    const sections = [
        { label: 'Fact', data: signal.fact },
        { label: 'Significance', data: signal.significance },
        { label: 'Validation', data: signal.validation },
        { label: 'Interpretation', data: signal.interpretation },
        { label: 'Session', data: signal.session },
    ].filter(s => s.data && Object.keys(s.data).length > 0);

    if (sections.length === 0) {
        return (
            <Typography sx={{ fontSize: '10px', color: 'text.disabled', px: 1.5, pb: 1 }}>
                No additional details
            </Typography>
        );
    }

    return (
        <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {sections.map(({ label, data }) => (
                <Box key={label} sx={{ borderRadius: 1, bgcolor: bg, p: 1 }}>
                    <Typography sx={{
                        fontSize: '9px', fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: '0.06em', color: 'text.disabled', mb: 0.5,
                    }}>
                        {label}
                    </Typography>
                    {Object.entries(data).map(([k, v]) => (
                        <Box key={k} sx={{ display: 'flex', gap: 0.5 }}>
                            <Typography sx={{ fontSize: '10px', color: 'text.disabled', minWidth: 90, flexShrink: 0 }}>
                                {k.replace(/_/g, ' ')}
                            </Typography>
                            <Typography sx={{ fontSize: '10px', color: 'text.primary', wordBreak: 'break-word' }}>
                                {typeof v === 'number' ? v.toFixed(3) : String(v ?? '—')}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            ))}
        </Box>
    );
}

// ─── Signal row ───────────────────────────────────────────────────────────────

interface RowProps {
    signal: SignalItem;
    showMarketBadge: boolean;
    isHighlighted: boolean;  // selected (persistent)
    isHovered: boolean;      // hovered from either surface
    isExpanded: boolean;
    onToggleExpand: (id: string) => void;
}

function SignalRow({
    signal,
    showMarketBadge,
    isHighlighted,
    isHovered,
    isExpanded,
    onToggleExpand,
}: RowProps) {
    const theme = useTheme();
    const { actions } = useSignalInteraction();
    const isOpen = signal.status === 'open';
    const strength = scoreStrength(signal.score);

    const typeLabel = SIGNAL_TYPE_LABELS[signal.signal_type] ?? signal.signal_type;

    const dirIcon = signal.direction === 'bullish'
        ? <TrendingUpIcon sx={{ fontSize: 11, color: 'success.main', flexShrink: 0 }} />
        : signal.direction === 'bearish'
            ? <TrendingDownIcon sx={{ fontSize: 11, color: 'error.main', flexShrink: 0 }} />
            : <RemoveIcon sx={{ fontSize: 11, color: 'text.disabled', flexShrink: 0 }} />;

    const strengthColor =
        strength === 'high' ? theme.palette.success.main
            : strength === 'med' ? theme.palette.warning.main
                : theme.palette.text.disabled;

    return (
        <Box
            onMouseEnter={() => actions.hover(signal.id)}
            onMouseLeave={() => actions.hover(null)}
            onClick={() => {
                if (isExpanded) {
                    onToggleExpand(signal.id);
                    actions.select(null);
                } else {
                    onToggleExpand(signal.id);
                    actions.select(signal.id);
                }
            }}
            sx={{
                py: 0.75,
                px: 1,
                cursor: 'pointer',
                borderBottom: 1,
                borderColor: alpha(theme.palette.divider, 0.3),
                bgcolor: isHighlighted
                    ? alpha(theme.palette.primary.main, 0.08)
                    : isHovered
                        ? alpha(theme.palette.action.hover, 0.08)
                        : 'transparent',
                transition: 'background-color 0.15s',
            }}
        >
            {/* Line 1: direction icon + type label + relative time — mirrors: dot + marketQuestion + timestamp */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: '2px' }}>
                {dirIcon}
                <Typography sx={{
                    fontSize: '11px', fontWeight: 600, flex: 1,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                    {typeLabel}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: 'text.disabled', fontFamily: 'monospace', flexShrink: 0 }}>
                    {formatRelativeTime(signal.opened_at)}
                </Typography>
            </Box>

            {/* Line 2: strength badge + score + open dot + exact date — mirrors: BUY/SELL badge + price + amount + address */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '10px' }}>
                <Box sx={{
                    px: 0.5, py: 0,
                    borderRadius: '3px',
                    bgcolor: alpha(strengthColor, 0.15),
                    border: '1px solid',
                    borderColor: alpha(strengthColor, 0.3),
                    flexShrink: 0,
                }}>
                    <Typography sx={{ fontSize: '9px', fontWeight: 700, color: strengthColor, textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.6 }}>
                        {strength}
                    </Typography>
                </Box>

                <Typography component="span" sx={{ fontSize: '10px', color: 'text.secondary', fontFamily: 'monospace' }}>
                    {signal.score.toFixed(2)}
                </Typography>

                {isOpen && (
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                )}

                {/* Exact date — mirrors address slot, pushed to the right */}
                <Typography sx={{
                    fontSize: '9px', color: 'text.disabled', fontFamily: 'monospace',
                    ml: 'auto', flexShrink: 0,
                }}>
                    {formatExactDate(signal.opened_at)}
                </Typography>
            </Box>

            {isExpanded && <SignalDetail signal={signal} />}
        </Box>
    );
}

// ─── Summary Header ───────────────────────────────────────────────────────────

interface SummaryHeaderProps {
    summary: SignalSummary;
    typeFilter: string | null;
    directionFilter: DirectionFilter;
    strengthFilter: StrengthFilter;
    openOnly: boolean;
    onTypeFilterChange: (type: string | null) => void;
    onDirectionFilterChange: (dir: DirectionFilter) => void;
    onStrengthFilterChange: (s: StrengthFilter) => void;
    onOpenOnlyChange: (v: boolean) => void;
}

function SummaryHeader({
    summary,
    typeFilter,
    directionFilter,
    strengthFilter,
    openOnly,
    onTypeFilterChange,
    onDirectionFilterChange,
    onStrengthFilterChange,
    onOpenOnlyChange,
}: SummaryHeaderProps) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Sort types by total count desc
    const sortedTypes = useMemo(
        () => [...summary.byType.entries()].sort((a, b) => b[1].total - a[1].total),
        [summary.byType],
    );

    // The widest bar = the type with the most signals (fills 100%)
    const typeBarMax = sortedTypes.length > 0 ? sortedTypes[0][1].total : 1;

    const biasTotal = summary.bullishCount + summary.bearishCount + summary.neutralCount;

    // Strength bar colors
    const strengthColors = {
        low: theme.palette.text.disabled,
        med: theme.palette.warning.main,
        high: theme.palette.success.main,
    };

    const strengthLabels = { low: 'Low', med: 'Med', high: 'High' };
    const strengthMax = Math.max(summary.byStrength.low, summary.byStrength.med, summary.byStrength.high, 1);

    return (
        <Box sx={{
            flexShrink: 0,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            zIndex: 1,
        }}>
            {/* ── Row 1: Bias strip ─────────────────────────────────────────── */}
            <Box sx={{ px: 1.5, pt: 0.875, pb: 0.625 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.disabled' }}>
                        Bias
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontSize: '9px', color: 'text.disabled' }}>
                            {summary.total} signals
                        </Typography>
                        {/* Open only toggle */}
                        <Box
                            onClick={() => onOpenOnlyChange(!openOnly)}
                            sx={{
                                px: 0.625, py: 0.125, borderRadius: '3px', cursor: 'pointer',
                                border: 1, borderColor: openOnly ? 'success.main' : alpha(theme.palette.divider, 0.8),
                                bgcolor: openOnly ? alpha(theme.palette.success.main, 0.12) : 'transparent',
                                transition: 'all 0.15s',
                            }}
                        >
                            <Typography sx={{ fontSize: '9px', fontWeight: 600, color: openOnly ? 'success.main' : 'text.disabled' }}>
                                Open only
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Gauge bar */}
                {biasTotal > 0 && (
                    <Box sx={{ display: 'flex', height: 8, borderRadius: 1, overflow: 'hidden', gap: '1px', mb: 0.625 }}>
                        {summary.bullishCount > 0 && (
                            <Box
                                onClick={() => onDirectionFilterChange(directionFilter === 'bullish' ? null : 'bullish')}
                                sx={{
                                    flex: summary.bullishCount,
                                    bgcolor: directionFilter === null || directionFilter === 'bullish'
                                        ? theme.palette.success.main
                                        : alpha(theme.palette.success.main, 0.25),
                                    cursor: 'pointer',
                                    transition: 'opacity 0.15s',
                                    '&:hover': { opacity: 0.8 },
                                }}
                            />
                        )}
                        {summary.neutralCount > 0 && (
                            <Box
                                onClick={() => onDirectionFilterChange(directionFilter === 'neutral' ? null : 'neutral')}
                                sx={{
                                    flex: summary.neutralCount,
                                    bgcolor: directionFilter === null || directionFilter === 'neutral'
                                        ? alpha(theme.palette.text.secondary, 0.4)
                                        : alpha(theme.palette.text.secondary, 0.12),
                                    cursor: 'pointer',
                                    transition: 'opacity 0.15s',
                                    '&:hover': { opacity: 0.8 },
                                }}
                            />
                        )}
                        {summary.bearishCount > 0 && (
                            <Box
                                onClick={() => onDirectionFilterChange(directionFilter === 'bearish' ? null : 'bearish')}
                                sx={{
                                    flex: summary.bearishCount,
                                    bgcolor: directionFilter === null || directionFilter === 'bearish'
                                        ? theme.palette.error.main
                                        : alpha(theme.palette.error.main, 0.25),
                                    cursor: 'pointer',
                                    transition: 'opacity 0.15s',
                                    '&:hover': { opacity: 0.8 },
                                }}
                            />
                        )}
                    </Box>
                )}

                {/* Bias legend */}
                <Box sx={{ display: 'flex', gap: 1.25 }}>
                    {summary.bullishCount > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                            <TrendingUpIcon sx={{ fontSize: 9, color: 'success.main' }} />
                            <Typography sx={{ fontSize: '9px', color: 'success.main', fontFamily: 'monospace' }}>
                                {summary.bullishPct}%
                            </Typography>
                        </Box>
                    )}
                    {summary.neutralCount > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                            <RemoveIcon sx={{ fontSize: 9, color: 'text.disabled' }} />
                            <Typography sx={{ fontSize: '9px', color: 'text.disabled', fontFamily: 'monospace' }}>
                                {biasTotal > 0 ? Math.round((summary.neutralCount / biasTotal) * 100) : 0}%
                            </Typography>
                        </Box>
                    )}
                    {summary.bearishCount > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                            <TrendingDownIcon sx={{ fontSize: 9, color: 'error.main' }} />
                            <Typography sx={{ fontSize: '9px', color: 'error.main', fontFamily: 'monospace' }}>
                                {summary.bearishPct}%
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* ── Row 2: By type (stacked bars) ─────────────────────────────── */}
            {sortedTypes.length > 0 && (
                <Box sx={{
                    px: 1.5, py: 0.75,
                    borderTop: 1, borderColor: alpha(theme.palette.divider, 0.4),
                }}>
                    <Typography sx={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.disabled', mb: 0.625 }}>
                        By type
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {sortedTypes.map(([type, stats]) => {
                            const isActive = typeFilter === type;
                            const widthPct = (stats.total / typeBarMax) * 100;

                            return (
                                <Box
                                    key={type}
                                    onClick={() => onTypeFilterChange(isActive ? null : type)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 0.75,
                                        cursor: 'pointer',
                                        opacity: typeFilter !== null && !isActive ? 0.4 : 1,
                                        transition: 'opacity 0.15s',
                                        '&:hover': { opacity: 1 },
                                    }}
                                >
                                    {/* Label */}
                                    <Typography sx={{
                                        fontSize: '9px', color: isActive ? 'text.primary' : 'text.secondary',
                                        fontWeight: isActive ? 700 : 400,
                                        minWidth: 68, flexShrink: 0,
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    }}>
                                        {SIGNAL_TYPE_SHORT[type] ?? type}
                                    </Typography>

                                    {/* Stacked bar: bullish / neutral / bearish */}
                                    <Box sx={{ flex: 1, height: 6, display: 'flex', borderRadius: 0.5, overflow: 'hidden', bgcolor: alpha(theme.palette.divider, 0.2) }}>
                                        <Box sx={{ width: `${widthPct}%`, display: 'flex', height: '100%', gap: '1px' }}>
                                            {stats.bullish > 0 && (
                                                <Box sx={{
                                                    flex: stats.bullish,
                                                    bgcolor: theme.palette.success.main,
                                                    opacity: 0.85,
                                                }} />
                                            )}
                                            {stats.neutral > 0 && (
                                                <Box sx={{
                                                    flex: stats.neutral,
                                                    bgcolor: alpha(theme.palette.text.secondary, 0.4),
                                                }} />
                                            )}
                                            {stats.bearish > 0 && (
                                                <Box sx={{
                                                    flex: stats.bearish,
                                                    bgcolor: theme.palette.error.main,
                                                    opacity: 0.85,
                                                }} />
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Count */}
                                    <Typography sx={{
                                        fontSize: '9px', fontFamily: 'monospace',
                                        color: isActive ? 'text.primary' : 'text.disabled',
                                        minWidth: 16, textAlign: 'right',
                                    }}>
                                        {stats.total}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* ── Row 3: By strength (micro-histogram) ─────────────────────── */}
            <Box sx={{
                px: 1.5, py: 0.75,
                borderTop: 1, borderColor: alpha(theme.palette.divider, 0.4),
                display: 'flex', alignItems: 'center', gap: 0.75,
            }}>
                <Typography sx={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.disabled', minWidth: 52 }}>
                    Strength
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.875, flex: 1 }}>
                    {(['low', 'med', 'high'] as const).map(bucket => {
                        const count = summary.byStrength[bucket];
                        const barH = strengthMax > 0 ? Math.max(3, Math.round((count / strengthMax) * 20)) : 3;
                        const isActive = strengthFilter === bucket;
                        const color = strengthColors[bucket];

                        return (
                            <Box
                                key={bucket}
                                onClick={() => onStrengthFilterChange(isActive ? null : bucket)}
                                sx={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.375,
                                    cursor: 'pointer',
                                    opacity: strengthFilter !== null && !isActive ? 0.35 : 1,
                                    transition: 'opacity 0.15s',
                                    '&:hover': { opacity: 1 },
                                }}
                            >
                                <Typography sx={{ fontSize: '8px', color: isActive ? color : 'text.disabled', fontFamily: 'monospace' }}>
                                    {count}
                                </Typography>
                                <Box sx={{
                                    width: 18, height: barH,
                                    bgcolor: isActive ? color : alpha(color, 0.35),
                                    borderRadius: '2px 2px 0 0',
                                    transition: 'background-color 0.15s',
                                }} />
                                <Typography sx={{ fontSize: '8px', color: isActive ? color : 'text.disabled', fontWeight: isActive ? 700 : 400 }}>
                                    {strengthLabels[bucket]}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                {/* Clear all filters shortcut — only when any filter is active */}
                {(typeFilter !== null || directionFilter !== null || strengthFilter !== null || openOnly) && (
                    <Box
                        onClick={() => {
                            onTypeFilterChange(null);
                            onDirectionFilterChange(null);
                            onStrengthFilterChange(null);
                            onOpenOnlyChange(false);
                        }}
                        sx={{
                            px: 0.5, py: 0.125, borderRadius: '3px', cursor: 'pointer',
                            border: 1, borderColor: alpha(theme.palette.divider, 0.6),
                            ml: 'auto',
                            '&:hover': { borderColor: 'text.disabled' },
                        }}
                    >
                        <Typography sx={{ fontSize: '8px', color: 'text.disabled' }}>
                            Clear
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

// ─── Skeleton / empty ─────────────────────────────────────────────────────────

function SignalsSkeleton() {
    return (
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[...Array(5)].map((_, i) => (
                <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Skeleton variant="text" width="60%" height={14} />
                    <Skeleton variant="text" width="40%" height={11} />
                </Box>
            ))}
        </Box>
    );
}

function SignalsEmpty({ showMarketBadge }: { showMarketBadge: boolean }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5, opacity: 0.35 }}>
            <InsightsIcon sx={{ fontSize: 28 }} />
            <Typography sx={{ fontSize: '11px', color: 'text.disabled', textAlign: 'center', px: 2 }}>
                {showMarketBadge
                    ? 'No market signals across this event'
                    : 'No market signals for this market'}
            </Typography>
        </Box>
    );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export function SignalsPanel({
    signals,
    loading,
    error,
    showMarketBadge = false,
}: SignalsPanelProps) {
    const { state, actions } = useSignalInteraction();
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [typeFilter, setTypeFilter] = useState<string | null>(null);
    const [directionFilter, setDirectionFilter] = useState<DirectionFilter>(null);
    const [strengthFilter, setStrengthFilter] = useState<StrengthFilter>(null);
    const [openOnly, setOpenOnly] = useState(false);
    const virtuosoRef = useRef<any>(null);

    const handleToggleExpand = useCallback((id: string) => {
        setExpandedId(prev => prev === id ? null : id);
    }, []);

    // ── Sync expandedId with external selection (e.g. chart click) ───────────
    useEffect(() => {
        if (state.selectedId && state.selectedId !== expandedId) {
            setExpandedId(state.selectedId);
        } else if (!state.selectedId && expandedId) {
            setExpandedId(null);
        }
        // Only react to external selection changes, not self-driven ones
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.selectedId]);

    // ── Filter to market signals ──────────────────────────────────────────────
    const marketSignals = useMemo(
        () => signals.filter(s => MARKET_SIGNAL_TYPES.has(s.signal_type)),
        [signals],
    );

    const summary = useMemo(() => computeSummary(marketSignals), [marketSignals]);

    // ── Apply interactive filters ─────────────────────────────────────────────
    const filteredSignals = useMemo(() => {
        let result = marketSignals;
        if (openOnly) result = result.filter(s => s.status === 'open');
        if (directionFilter) result = result.filter(s =>
            directionFilter === 'neutral'
                ? s.direction == null
                : s.direction === directionFilter,
        );
        if (typeFilter) result = result.filter(s => s.signal_type === typeFilter);
        if (strengthFilter) result = result.filter(s => scoreStrength(s.score) === strengthFilter);
        return result.slice().reverse();
    }, [marketSignals, openOnly, directionFilter, typeFilter, strengthFilter]);

    // ── Publish visible IDs to context so the chart can dim excluded pins ─────
    const anyFilterActive = openOnly || !!directionFilter || !!typeFilter || !!strengthFilter;
    useEffect(() => {
        if (anyFilterActive) {
            actions.setVisibleIds(new Set(filteredSignals.map(s => s.id)));
        } else {
            actions.setVisibleIds(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anyFilterActive, filteredSignals]);

    /*
    // ── Scroll to externally hovered row ─────────────────────────────────────
    useEffect(() => {
        if (!state.hoveredId) return;
        const idx = filteredSignals.findIndex(s => s.id === state.hoveredId);
        if (idx !== -1) {
            virtuosoRef.current?.scrollToIndex({ index: idx, behavior: 'smooth', align: 'center' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.hoveredId]);
    */

    if (loading) return <SignalsSkeleton />;
    if (error) return (
        <Box sx={{ p: 2 }}>
            <Typography sx={{ fontSize: '11px', color: 'error.main' }}>Failed to load signals</Typography>
        </Box>
    );
    if (marketSignals.length === 0) return <SignalsEmpty showMarketBadge={showMarketBadge} />;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <SummaryHeader
                summary={summary}
                typeFilter={typeFilter}
                directionFilter={directionFilter}
                strengthFilter={strengthFilter}
                openOnly={openOnly}
                onTypeFilterChange={setTypeFilter}
                onDirectionFilterChange={setDirectionFilter}
                onStrengthFilterChange={setStrengthFilter}
                onOpenOnlyChange={setOpenOnly}
            />

            {filteredSignals.length === 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, opacity: 0.4 }}>
                    <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                        No signals match filter
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ flex: 1, position: 'relative' }}>
                    <Virtuoso
                        ref={virtuosoRef}
                        style={{ height: '100%', overscrollBehavior: 'contain' }}
                        data={filteredSignals}
                        overscan={200}
                        className="hide-scrollbar"
                        itemContent={(_index: number, signal: SignalItem) => (
                            <SignalRow
                                key={signal.id}
                                signal={signal}
                                showMarketBadge={showMarketBadge}
                                isHighlighted={signal.id === state.selectedId}
                                isHovered={signal.id === state.hoveredId}
                                isExpanded={signal.id === expandedId}
                                onToggleExpand={handleToggleExpand}
                            />
                        )}
                    />
                    <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; } .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}</style>
                </Box>
            )}
        </Box>
    );
}